import { Fragment } from 'react'
import escapeRegExp from 'lodash/escapeRegExp'
import styled from 'styled-components'
import { Body } from '@shipt/design-system-typography'

export const getBoldedWords = (query: string, text: string) => {
  const trimmedQuery = query.trim()
  const escapedQuery = escapeRegExp(trimmedQuery) // Must be escaped or app crashes
  const queryDetectionRegex = [
    new RegExp(`^${escapedQuery}`, 'i'), // detects suggestion starting
    new RegExp(` ${escapedQuery}`, 'i'), // detects beginning of word in suggestion
  ]

  const foundRegex = queryDetectionRegex.find((autocompleteRegex) =>
    Boolean(autocompleteRegex.exec(text)),
  )
  let initialIndex = 0
  let endIndex = text.length

  if (foundRegex) {
    const foundOccurrence = foundRegex.exec(text)
    initialIndex = foundOccurrence?.index ?? 0

    if (queryDetectionRegex[1] === foundRegex) {
      // off by one issue with regex detecting the space
      initialIndex++
    }

    endIndex = initialIndex + trimmedQuery.length
  }

  const first = text.substring(0, initialIndex)
  const second = text.substring(initialIndex, endIndex)
  const third = text.substring(endIndex, text.length)

  return [
    {
      text: first,
      isBold: true,
    },
    {
      text: second,
      isBold: false,
    },
    {
      text: third,
      isBold: true,
    },
  ]
}

type Props = {
  text: string
  emphasis: string
}
export const PartialEmphasis = ({ text = '', emphasis = '', ...restProps }: Props) => {
  const lowerText = text.toLowerCase()

  const words = getBoldedWords(emphasis, lowerText)

  const fragments = words
    .filter((w) => Boolean(w.text))
    .map((word, i) => (
      // Index is the only guaranteed unique value available
      // oxlint-disable-next-line react/no-array-index-key
      <Fragment key={i}>
        {word.isBold && emphasis.trim() ? <EmphasisSpan>{word.text}</EmphasisSpan> : word.text}
      </Fragment>
    ))

  // This uses a hack so that iOS Voiceover
  // will not read things like "app" "les"
  // instead of "apples". Both the non-standard
  // role and the aria-label are necessary for
  // iOS Voiceover to behave correctly.
  return (
    <PartialEmphasisDiv as="div" {...restProps} role="text" aria-label={lowerText}>
      {fragments}
    </PartialEmphasisDiv>
  )
}

const EmphasisSpan = styled.span`
  font-weight: 600;
`

const PartialEmphasisDiv = styled(Body)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  list-style-type: none;
`
